/* eslint-disable no-underscore-dangle */
import React, { useContext, useEffect, useState } from 'react';
import { Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { Formik } from 'formik';
import ErrorBoundary from 'components/common/ErrorBoundary';
import { dateToMilliseconds, validationSchema } from 'pages/Orders/constant';
import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_PAYMENT_TRANSITION,
  GET_ORDER_BY_ID,
  UPDATE_ORDER,
  UPDATE_ORDER_STATUS,
} from 'pages/Orders/OrdersGraphQL';
import FSBModal from 'components/common/FSBModal';
import { convertDateTimeIntoEpochTime } from 'pages/OfferCoupons/constants';
import { axiosGetAPI, axiosPost } from 'services';
import { authContext } from 'contexts/AuthContext';
import Loader from 'components/common/Loader';

import { getOrderDraft } from 'pages/Orders/orderDraftStore';
import EditOrderForm from './components/EditOrderForm';

const EditOrder = ({
  orderInitialValues,
  onCloseOrderPopup = () => {},
  onOrderUpdated = () => {},
  sections,
  validateTimestamp,
}) => {
  const { auth } = useContext(authContext);

  const [proofLoading, setProofLoading] = useState(false);
  const [showProof, setShowProof] = useState({
    show: false,
    proofURL: undefined,
    utr: undefined,
  });
  const [loading, setLoading] = useState(false);
  const [initialValues, setInitialValues] = useState(null);
  const [orderId, setOrderId] = useState(null);

  const [updateOrder] = useMutation(UPDATE_ORDER, {
    onCompleted: (data) => {
      if (data) {
        toast.success(data?.updateOrder);
        onCloseOrderPopup();
      }
    },
    onError: ({ graphQLErrors }) => {
      graphQLErrors.map(({ exception }) => {
        toast.error(exception?.message);
        return exception;
      });

      onCloseOrderPopup();
    },
  });

  const [updateOrderV2] = useMutation(UPDATE_ORDER, {
    onCompleted: () => {},
    onError: () => {},
  });

  const [updateOrderStatus] = useMutation(UPDATE_ORDER_STATUS, {
    onError: ({ graphQLErrors }) => {
      graphQLErrors.map(({ exception, message }) => {
        toast.error(exception?.message || message);
        return exception || message;
      });
    },
  });

  const {
    data: orderData,
    refetch: refetchOrderData,
    loading: orderDataLoading,
  } = useQuery(GET_ORDER_BY_ID, {
    variables: {
      id: orderId,
    },
    skip: !orderId,
    fetchPolicy: 'network',
  });

  const fetchOrderInvoices = async (order) => {
    if (!order?.id) return order;
    try {
      const resp = await axiosGetAPI(
        `${process.env.REACT_APP_REST_API_ENDPOINT}invoice/${order.id}`,
        auth,
      );
      if (resp?.isSuccessful && resp?.data?.data) {
        const invoices = resp.data.data;
        const formattedInvoiceInfo = (order.orderLogistics || []).map((logistic) => {
          const savedInvoice = invoices.find(
            (inv) => String(inv?.logisticId) === String(logistic?.id),
          );
          if (savedInvoice) {
            let parsedFiles = null;
            if (savedInvoice?.invoiceFiles) {
              if (Array.isArray(savedInvoice.invoiceFiles)) {
                parsedFiles = savedInvoice.invoiceFiles;
              } else if (typeof savedInvoice.invoiceFiles === 'string') {
                try {
                  parsedFiles = JSON.parse(savedInvoice.invoiceFiles);
                } catch (e) {
                  parsedFiles = [savedInvoice.invoiceFiles];
                }
              } else {
                parsedFiles = [savedInvoice.invoiceFiles];
              }
            }
            return {
              paymentStatus: savedInvoice?.paymentStatus
                ? {
                    id: savedInvoice.paymentStatus,
                    label:
                      savedInvoice.paymentStatus === 'PAID'
                        ? 'Paid'
                        : 'Pending',
                  }
                : null,
              order: order.id,
              id: savedInvoice?.id || null,
              invoiceFiles: parsedFiles,
              total: savedInvoice?.total || null,
              releasedToUser: savedInvoice?.releasedToUser || null,
              logisticId: logistic?.id,
            };
          }
          return {
            paymentStatus: null,
            order: order.id,
            id: null,
            invoiceFiles: null,
            total: null,
            releasedToUser: null,
          };
        });
        return {
          ...order,
          invoiceInfo: formattedInvoiceInfo.length ? formattedInvoiceInfo : undefined,
        };
      }
    } catch (e) {
      console.error('Error fetching order invoices in EditOrder:', e);
    }
    return order;
  };

  useEffect(() => {
    let isMounted = true;
    const loadInitialValues = async () => {
      const order = orderData?.order || orderInitialValues;
      if (!order) return;
      const orderWithInvoices = await fetchOrderInvoices(order);
      if (isMounted) {
        setInitialValues(orderWithInvoices);
      }
    };
    loadInitialValues();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData, orderInitialValues]);

  useEffect(() => {
    if (orderInitialValues?.id) {
      setOrderId(orderInitialValues?.id);
    }
  }, [orderInitialValues?.id]);

  const [createPaymentTransition] = useMutation(CREATE_PAYMENT_TRANSITION, {
    // eslint-disable-next-line no-shadow
    onCompleted: (data) => {
      if (data) {
        toast.success('Status updated successfully');
        onCloseOrderPopup();
      }
    },
    onError: ({ graphQLErrors }) => {
      graphQLErrors.map(({ exception }) => {
        toast.error(exception?.message);
        return exception;
      });
    },
  });

  const processOrderValues = (values) => {
    const order = JSON.parse(JSON.stringify(values));
    order.offer = order.offer?.id;
    order.demand = order.demand?.id;
    order.grade = order.grade?.id;
    order.seller = order.seller?.id;
    order.buyer = order.buyer?.id;
    order.rateUnit = order.rateUnit?.id;
    order.quantityUnit = order.quantityUnit?.id;
    order.freightValue = Number(order.freightValue);
    order.loadingValue = Number(order.loadingValue);

    if (order?.billingAddress?.id) {
      delete order.billingAddress.__typename;
      if (order.billingAddress?.city?.id)
        delete order.billingAddress.city.__typename;
      if (order.billingAddress?.state?.id)
        delete order.billingAddress.state.__typename;
      if (order.billingAddress?.country?.id)
        delete order.billingAddress.country.__typename;
      order.billingAddress = {
        user: order?.buyer,
        id: order.billingAddress?.id,
        area: order?.billingAddress?.area,
        street: order?.billingAddress?.street,
        pinCode: order?.billingAddress?.pinCode,
        city:
          order.billingAddress?.city?.id ??
          order.billingAddress?.cityId ??
          order.billingAddress?.city,
        state:
          order.billingAddress?.state?.id ??
          order.billingAddress?.stateId ??
          order.billingAddress?.state,
        country:
          order.billingAddress?.country?.id ??
          order.billingAddress?.countryId ??
          order.billingAddress?.country,
      };
    }

    if (order?.bid?.id) {
      order.bid = order?.bid?.id;
    }

    if (order?.newDeliveryAddress) {
      order.deliveryAddress = {
        user: order?.buyer,
        id: order.newDeliveryAddress?.id,
        city: order.newDeliveryAddress?.city?.id,
        country: order.newDeliveryAddress?.country?.id,
        state: order.newDeliveryAddress?.state?.id,
        street: order.newDeliveryAddress?.street,
        area: order.newDeliveryAddress?.area,
        pinCode: order.newDeliveryAddress?.pinCode,
        houseNumber: order.newDeliveryAddress?.houseNumber,
      };
      delete order.newDeliveryAddress.__typename;
    } else {
      order.deliveryAddress = {
        user: order?.buyer,
        id: order.deliveryAddress?.id || order?.deliveryAddress?.id,
        city:
          order.deliveryAddress?.city?.id ??
          order.deliveryAddress?.cityId ??
          order.deliveryAddress?.city,
        state:
          order.deliveryAddress?.state?.id ??
          order.deliveryAddress?.stateId ??
          order.deliveryAddress?.state,
        country:
          order.deliveryAddress?.country?.id ??
          order.deliveryAddress?.countryId ??
          order.deliveryAddress?.country,
        street: order.deliveryAddress?.street,
        area: order.deliveryAddress?.area,
        pinCode: order.deliveryAddress?.pinCode,
        houseNumber: order.deliveryAddress?.houseNumber,
      };
      delete order.deliveryAddress.__typename;
    }

    for (let i = 0; i < order.orderLogistics.length; i += 1) {
      const Order = order.orderLogistics[i];
      Order.order = values.id;
      Order.rateUnit = values.rateUnit?.id;
      Order.callingCode = values?.orderLogistics?.[i]?.callingCode?.id
        ? values?.orderLogistics?.[i]?.callingCode?.id
        : null;
      Order.loadingImage = values?.orderLogistics?.[i]?.loadingImage;
      Order.lrDoc = values?.orderLogistics?.[i]?.lrDoc;
      Order.weightSlipDoc = values?.orderLogistics?.[i]?.weightSlipDoc;
      Order.vehicleNumberImage =
        values?.orderLogistics?.[i]?.vehicleNumberImage;
      Order.deliveryDate = convertDateTimeIntoEpochTime(
        values?.orderLogistics?.[i]?.deliveryDate,
      );
      delete Order.__typename;
      delete Order.invoice;
      // if (!Order?.id) delete Order.id;

      if (Order.pickupAddress) {
        Order.pickupAddress = {
          id: Order.pickupAddress?.id || null,
          city: Order.pickupAddress?.city?.id || null,
          country: Order.pickupAddress?.country?.id || null,
          state: Order.pickupAddress?.state?.id || null,
          street: Order.pickupAddress?.street || null,
          area: Order.pickupAddress?.area || null,
          houseNumber: Order.pickupAddress?.houseNumber || null,
          pinCode: Order.pickupAddress?.pinCode || null,
          user: order?.seller || null,
        };

        delete Order.pickupAddress.__typename;
      }
    }

    if (order?.newDeliveryAddress) {
      delete order.newDeliveryAddress;
    }
    if (!order?.deliveryAddress?.id) {
      order.deliveryAddress = null;
    }

    delete order.city;
    delete order.state;
    delete order.portOfDischarge;
    delete order.portOfLoading;
    delete order.convenienceAmount;
    delete order.createdAt;
    delete order.dueAmount;
    delete order.gstAmount;
    delete order.paymentTransitions;
    delete order.prepayment;
    delete order.serviceTaxAmount;
    delete order.subtotal;
    delete order.tdsAmount;
    delete order.total;
    delete order.orderLogisticStatus;
    delete order.pendingQuantity;
    delete order.buyerGstBillingAddress;
    delete order.__typename;
    delete order.createdByUserId;
    delete order.updatedByUserId;
    delete order.sellerCommissionAmount;
    delete order.foreignDemand;
    delete order.foreignOffer;
    delete order.forwardDemand;
    delete order.forwardOffer;
    delete order.deliveredQuantity;
    delete order.addressInfo;
    delete order.invoiceInfo;
    delete order.paymentDetails;
    delete order.sellerCommissionJson;
    delete order.buyerCommissionJson;
    delete order.buyerCommissionAmount;
    delete order.sellerCommissionAmount;

    return order;
  };

  const handleShowProof = (proofURL, utr) => {
    setProofLoading(true);
    setShowProof({
      show: true,
      proofURL,
      utr,
    });
  };
  const handleHideProof = () => {
    setShowProof({
      show: false,
      proofURL: undefined,
      utr: undefined,
    });
  };

  const handleSubmit = (values) => {
    const order = processOrderValues(values);
    updateOrder({
      variables: {
        order,
      },
    });
  };

  const handleCharges = (values) => {
    const order = processOrderValues(values);
    updateOrderV2({
      variables: {
        order,
      },
    });
  };

  const handleAddress = (name, address, values) => {
    const order = processOrderValues({ ...values, [name]: address });
    updateOrderV2({
      variables: {
        order,
      },
    });
  };

  const handleOrderLogistics = async (values) => {
    const order = JSON.parse(JSON.stringify(values));
    const selectedOrderStatus =
      order?.orderLogistics?.[order.orderLogistics.length - 1]
        ?.orderLogisticStatus;

    for (let i = 0; i < order.orderLogistics.length; i += 1) {
      const Order = order.orderLogistics[i];
      Order.order = values.id;
      Order.rateUnit = values.rateUnit?.id;
      Order.callingCode = values?.orderLogistics?.[i]?.callingCode?.callingCode
        ? values?.orderLogistics?.[i]?.callingCode?.callingCode
        : null;
      Order.loadingImage = values?.orderLogistics?.[i]?.loadingImage;
      Order.lrDoc = values?.orderLogistics?.[i]?.lrDoc;
      Order.weightSlipDoc = Array.isArray(
        values?.orderLogistics?.[i]?.weightSlipDoc,
      )
        ? values?.orderLogistics?.[i]?.weightSlipDoc
        : [values?.orderLogistics?.[i]?.weightSlipDoc];
      Order.vehicleNumberImage =
        values?.orderLogistics?.[i]?.vehicleNumberImage;
      Order.deliveryDate = values?.orderLogistics?.[i]?.deliveryDate
        ? convertDateTimeIntoEpochTime(
            values?.orderLogistics?.[i]?.deliveryDate,
          )
        : null;
      Order.loadingCharges = 0.0;
      Order.freight = 0.0;
      Order.logisticDeliveryTerm =
        values?.orderLogistics?.[i]?.logisticDeliveryTerm || '';
      delete Order.__typename;
      delete Order.invoice;
      // if (!Order?.id) delete Order.id;

      if (Order.pickupAddress) {
        Order.pickupAddress = {
          id: Order.pickupAddress?.id || null,
          city: Order.pickupAddress?.city?.id || null,
          country: Order.pickupAddress?.country?.id || null,
          state: Order.pickupAddress?.state?.id || null,
          street: Order.pickupAddress?.street || '',
          area: Order.pickupAddress?.area || '',
          houseNumber: Order.pickupAddress?.houseNumber || '',
          pinCode: Order.pickupAddress?.pinCode || '',
          user: order?.seller?.id || '',
        };

        delete Order.pickupAddress.__typename;
      }
    }

    try {
      setLoading(true);
      await axiosPost(
        `${process.env.REACT_APP_REST_API_ENDPOINT}order/process-order-logistics/${values?.id}`,
        [...(order?.orderLogistics || [])],
        {},
        auth,
      );
      if (selectedOrderStatus && selectedOrderStatus !== values?.status) {
        const deliveredQty = Math.round(
          Number(
            values?.quantity ||
            orderData?.order?.quantity ||
            orderInitialValues?.quantity ||
            0,
          ),
        );

        if (
          selectedOrderStatus === 'DELIVERED' &&
          (!deliveredQty || deliveredQty <= 0)
        ) {
          toast.error('Please enter valid delivered quantity');
          return;
        }

        await updateOrderStatus({
          variables: {
            id: values?.id,
            status: selectedOrderStatus,
            deliveredQuantity:
              selectedOrderStatus === 'DELIVERED'
                ? deliveredQty
                : null,
          },
        });
      }
      onOrderUpdated();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      refetchOrderData();
    }
  };

  if (orderDataLoading || !initialValues) {
    return <Loader />;
  }

  return (
    <div>
      <FSBModal
        open={showProof.show}
        title={`Payment Proof - ${showProof.utr}`}
        message={
          <div className="d-flex align-items-center justify-content-center">
            {proofLoading && <Spinner />}

            <img
              src={showProof.proofURL}
              style={{ maxHeight: 450, maxWidth: 450 }}
              onLoad={() => {
                setProofLoading(false);
              }}
              alt={`Failed to load - payment proof for UTR number ${showProof.utr}`}
            />
          </div>
        }
        onClose={handleHideProof}
      >
        {/* Need this text to disable es lint error */}
      </FSBModal>
      <ErrorBoundary>
        <Formik
          enableReinitialize
          validationSchema={validationSchema}
          initialValues={(() => {
            const currentOrderId = initialValues?.id || orderInitialValues?.id;
            const draft = getOrderDraft(currentOrderId);
            const draftLogistics =
              draft?.orderLogistics && draft.orderLogistics.length > 0
                ? draft.orderLogistics
                : null;
            const draftInvoice =
              draft?.invoiceInfo && draft.invoiceInfo.length > 0
                ? draft.invoiceInfo
                : null;

            return {
              ...orderInitialValues,
              ...initialValues,
              orderLogistics:
                draftLogistics ||
                initialValues?.orderLogistics ||
                orderInitialValues?.orderLogistics ||
                [],
              invoiceInfo:
                draftInvoice ||
                (initialValues?.invoiceInfo && initialValues?.invoiceInfo?.length > 0
                  ? initialValues?.invoiceInfo
                  : (initialValues?.orderLogistics?.length
                      ? initialValues?.orderLogistics?.map((item) => ({
                          paymentStatus: null,
                          order: initialValues?.id,
                          id: null,
                          invoiceFiles: null,
                          total: null,
                          releasedToUser: null,
                        }))
                      : [
                          {
                            paymentStatus: null,
                            order: initialValues?.id,
                            id: null,
                            invoiceFiles: null,
                            total: null,
                            releasedToUser: null,
                          },
                        ])),
              rateUnit: {
                id: initialValues?.rateUnit?.id || orderInitialValues?.rateUnit?.id,
                label:
                  initialValues?.rateUnit?.isoCurrencyCode ||
                  orderInitialValues?.rateUnit?.isoCurrencyCode,
              },
              quantityUnit: {
                id: initialValues?.quantityUnit || orderInitialValues?.quantityUnit,
                label:
                  initialValues?.quantityUnit || orderInitialValues?.quantityUnit,
              },
            };
          })()}
          onSubmit={handleOrderLogistics}
          children={(props) => {
            const logisticSetting = [
              props.values?.isLogisticSelf,
              orderData?.order?.isLogisticSelf,
              orderInitialValues?.isLogisticSelf,
            ].find((value) => value !== undefined && value !== null && value !== '');
            const isLogisticSelf =
              logisticSetting === true || logisticSetting === 'true';
            const hasLogisticSelection =
              isLogisticSelf || logisticSetting === false || logisticSetting === 'false';
            return (
              <EditOrderForm
                isChargeFormDisabled={!hasLogisticSelection || isLogisticSelf}
                isLogisticSelf={logisticSetting}
                onCloseOrderPopup={onCloseOrderPopup}
                sections={sections}
                handleShowProof={handleShowProof}
                handleAddress={handleAddress}
                handleCharges={handleCharges}
                loading={loading}
                orderData={orderData}
                refetchOrderData={refetchOrderData}
                onOrderUpdated={onOrderUpdated}
                validateTimestamp={validateTimestamp}
                {...props}
              />
            );
          }}
        />
      </ErrorBoundary>
    </div>
  );
};

export default EditOrder;
